import { useState, useEffect } from "react";
import axios from "axios";
import { ShoppingBag, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;

const ProductCard = ({ p, qty, setQty, canBuy }) => (
  <div className="flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm" data-testid={`shop-product-${p.id}`}>
    {p.image_url && <img src={p.image_url} alt={p.name} className="h-48 w-full object-cover" loading="lazy" />}
    <div className="flex flex-1 flex-col p-5">
      <h3 className="font-semibold text-slate-900">{p.name}</h3>
      {p.description && <p className="mt-1 flex-1 text-sm text-slate-600">{p.description}</p>}
      <div className="mt-4 flex items-center justify-between">
        <span className="text-lg font-bold text-slate-900">{eur(p.price)}</span>
        {canBuy && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setQty(Math.max(0, qty - 1))} className="h-8 w-8 rounded-full border border-slate-200 text-slate-700" data-testid={`shop-minus-${p.id}`}>−</button>
            <span className="w-5 text-center tabular-nums" data-testid={`shop-qty-${p.id}`}>{qty}</span>
            <button type="button" onClick={() => setQty(Math.min(20, qty + 1))} className="h-8 w-8 rounded-full border border-slate-200 text-slate-700" data-testid={`shop-plus-${p.id}`}>+</button>
          </div>
        )}
      </div>
    </div>
  </div>
);

export const ShopBlock = ({ pageId, isPreview, buttonClass }) => {
  const [shop, setShop] = useState(null);
  const [cart, setCart] = useState({});
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!pageId) return;
    axios.get(`${API}/shop/${pageId}`).then((r) => setShop(r.data)).catch(() => {});
    const sid = new URLSearchParams(window.location.search).get("bestellung");
    if (sid) axios.get(`${API}/shop/orders/status/${sid}`).then((r) => setPaid(r.data.status === "paid")).catch(() => {});
  }, [pageId]);

  if (!shop?.products?.length) return null;
  const canBuy = shop.checkout_enabled && !isPreview;
  const items = Object.entries(cart).filter(([, q]) => q > 0).map(([product_id, quantity]) => ({ product_id, quantity }));
  const sum = items.reduce((s, i) => s + i.quantity * (shop.products.find((p) => p.id === i.product_id)?.price || 0), 0);

  const checkout = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/shop/${pageId}/checkout`, { items, origin_url: window.location.origin + window.location.pathname });
      window.location.href = data.url;
    } catch (e) { setError(e.response?.data?.detail || "Bezahlung konnte nicht gestartet werden."); setBusy(false); }
  };

  return (
    <section id="shop" className="bg-slate-50 py-16 sm:py-20" data-testid="shop-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 flex items-center gap-3">
          <ShoppingBag className="h-7 w-7 text-slate-900" />
          <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">Unsere Produkte</h2>
        </div>
        {paid && (
          <div className="mb-8 flex items-center gap-2 rounded-xl bg-emerald-50 p-4 text-emerald-800 ring-1 ring-emerald-200" data-testid="shop-order-success">
            <CheckCircle2 className="h-5 w-5" /> Vielen Dank für Ihre Bestellung! Die Zahlung war erfolgreich, Sie erhalten eine Bestätigung per E-Mail.
          </div>
        )}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {shop.products.map((p) => <ProductCard key={p.id} p={p} canBuy={canBuy} qty={cart[p.id] || 0} setQty={(q) => setCart((c) => ({ ...c, [p.id]: q }))} />)}
        </div>
        {canBuy && items.length > 0 && (
          <div className="sticky bottom-4 mt-8 flex flex-col items-center justify-between gap-3 rounded-2xl bg-white p-4 shadow-xl ring-1 ring-slate-200 sm:flex-row" data-testid="shop-cart-bar">
            <span className="text-sm text-slate-700">
              Summe: <strong data-testid="shop-cart-total">{eur(sum)}</strong>{shop.shipping_fee > 0 && ` + ${eur(shop.shipping_fee)} Versand`}
            </span>
            <Button onClick={checkout} disabled={busy} className={`rounded-full text-white ${buttonClass}`} data-testid="shop-checkout-btn">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Jetzt sicher bezahlen
            </Button>
          </div>
        )}
        {error && <p className="mt-3 text-sm text-red-600" data-testid="shop-error">{error}</p>}
      </div>
    </section>
  );
};
